import type { AskWithMultiDocsParams } from "@mirage/service-conversation/router_state_types";
import { CtaSource } from "@mirage/service-conversation/router_state_types";
import { buildConversationRouterState } from "@mirage/service-conversation/utils/buildConversationRouterState";
import { startStackChat } from "@mirage/service-fss-bridge";
import type { FSSFile } from "@mirage/service-fss-bridge/service/bridge_types";
import { useTheme } from "@mirage/service-settings/theming/useTheme";
import type { StackItemShortcutWithMetadata } from "@mirage/service-stacks/service/types";
import { stackItemGetName } from "@mirage/service-stacks/service/utils";
import { faviconSrcForSrcUrl } from "@mirage/shared/util/favicon";
import {
  activeStackAtom,
  activeStackItemShortcutsAtom,
} from "@mirage/stacks/atoms";
import { useSortedStacks } from "@mirage/stacks/hooks";
import { useStackChatSources } from "@mirage/stacks/hooks/FullScreenStackHooks";
import { isFssStack } from "@mirage/stacks/utils";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { stackChatSidebarOpenAtom } from "../atoms/chatSidebarAtoms";

// When user clicks "Ask" (ask Dash) on stack details page, we load the chat sidebar.
// We have different handling logic for FSS and Dash stacks.
export function useLoadStackAskDashChat() {
  const stack = useAtomValue(activeStackAtom);
  const items = useAtomValue(activeStackItemShortcutsAtom);
  const setStackChatSidebarOpen = useSetAtom(stackChatSidebarOpenAtom);
  const loadDashStackChat = useLoadDashStackChat();

  return useCallback(() => {
    if (!stack?.sharing_id) {
      return;
    }
    if (isFssStack(stack)) {
      const fileIds = items ? convertItemsToFSSFiles(items) : [];
      // send message to FSS (parent frame)
      startStackChat(fileIds);
    } else {
      // Dash stack
      setStackChatSidebarOpen(true);
      loadDashStackChat();
    }
  }, [stack, items, setStackChatSidebarOpen, loadDashStackChat]);
}

// For Dash Stack chat
export function useLoadDashStackChat() {
  const navigate = useNavigate();
  const location = useLocation();
  const stack = useAtomValue(activeStackAtom);
  const { chatSources } = useStackChatSources();

  return useCallback(() => {
    if (!stack?.sharing_id || !chatSources.length) {
      return;
    }
    // Setup router state for Dash chat
    const state = buildConversationRouterState(CtaSource.StackChat, {
      chatSources,
      scenarioMeta: {
        type: "stack",
        sharingId: stack.sharing_id,
        namespaceId: stack?.namespace_id || "",
      },
    } satisfies AskWithMultiDocsParams);
    // Stays on stacks detail page
    navigate(location.pathname, {
      replace: true,
      state,
    });
  }, [stack, navigate, location.pathname, chatSources]);
}

// When user clicks "Ask & summarize" for an item on stack details page, we load the chat sidebar.
// We have different handling logic for FSS and Dash stacks.
export function useLoadStackItemSummarizeChat() {
  const stack = useAtomValue(activeStackAtom);
  const setStackChatSidebarOpen = useSetAtom(stackChatSidebarOpenAtom);
  const loadDashStackItemSummarizeChat = useLoadDashStackItemSummarizeChat();

  return useCallback(
    (item: StackItemShortcutWithMetadata) => {
      if (isFssStack(stack)) {
        // TODO(BFD-1306): implement logic handling for FSS items
      } else {
        // Dash stack
        setStackChatSidebarOpen(true);
        loadDashStackItemSummarizeChat(item);
      }
    },
    [stack, setStackChatSidebarOpen, loadDashStackItemSummarizeChat],
  );
}

// For Dash Stack item summarize chat
function useLoadDashStackItemSummarizeChat() {
  const stack = useAtomValue(activeStackAtom);
  const navigate = useNavigate();
  const location = useLocation();
  const allStacks = useSortedStacks();
  const isDarkMode = useTheme();

  return useCallback(
    (item: StackItemShortcutWithMetadata) => {
      if (!stack?.sharing_id) {
        return;
      }
      // Setup router state for Dash chat
      const state = buildConversationRouterState(CtaSource.StackItemSummarize, {
        chatSources: [
          {
            type: "stack_item",
            stackItem: {
              ...item,
              name: stackItemGetName(item, allStacks),
              faviconUrl: item.url
                ? faviconSrcForSrcUrl(item.url, 32, isDarkMode)
                : item.faviconUrl,
            },
          },
        ],
        scenarioMeta: {
          type: "stack_item_summarize",
          stackSharingId: stack.sharing_id,
        },
      });
      // Stays on stacks detail page
      navigate(location.pathname, {
        replace: true,
        state,
      });
    },
    [stack, allStacks, isDarkMode, navigate, location.pathname],
  );
}

export const convertItemsToFSSFiles = (
  items: StackItemShortcutWithMetadata[],
): FSSFile[] => {
  if (!items || items.length === 0) return [];
  return items
    .filter(
      (item) =>
        !!item?.id_3p &&
        // FSS files only
        item.branded_type &&
        ["DropboxFile", "DropboxPaper"].includes(item.branded_type),
    )
    .map(
      (item): FSSFile => ({
        type: "file",
        fileId: item.id_3p!,
        name: item.metadata_title || "",
        href: item.url || undefined,
      }),
    );
};
